import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/currency';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  LogOut, Menu, X, Image as ImageIcon, FileText, FileImage,
  Filter, XCircle, Download, TrendingDown, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReceiptsPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);

  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [facets, setFacets] = useState({ months: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [filterMonth, setFilterMonth] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [openTx, setOpenTx] = useState(null);
  const [openContent, setOpenContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterMonth !== 'all') params.set('month', filterMonth);
      if (filterCategory !== 'all') params.set('category', filterCategory);
      if (filterType !== 'all') params.set('type', filterType);
      const url = `${API_URL}/api/receipts${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setFacets(data.facets || { months: [], categories: [] });
      }
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterCategory, filterType]);

  const fetchAccounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAccounts(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const accountMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const openReceipt = async (tx) => {
    setOpenTx(tx);
    setOpenContent(null);
    setContentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/receipts/${tx.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setOpenContent(await res.json());
      }
    } finally {
      setContentLoading(false);
    }
  };

  const closeReceipt = () => {
    setOpenTx(null);
    setOpenContent(null);
  };

  const downloadReceipt = () => {
    if (!openContent?.receipt_base64) return;
    const a = document.createElement('a');
    a.href = openContent.receipt_base64;
    const ext = openContent.receipt_base64.startsWith('data:application/pdf') ? 'pdf' : 'jpg';
    a.download = `recibo_${openContent.id}.${ext}`;
    a.click();
  };

  const clearFilters = () => {
    setFilterMonth('all');
    setFilterCategory('all');
    setFilterType('all');
  };

  const hasFilters = filterMonth !== 'all' || filterCategory !== 'all' || filterType !== 'all';
  const total = items.reduce((s, t) => s + (t.amount || 0), 0);
  const totalExpense = items.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="receipts" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="receipts" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex items-center gap-3 mb-6">
          <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
            <Menu className="h-6 w-6" />
          </button>
          <div className="min-w-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Galería de recibos</h2>
            <p className="text-zinc-400 text-sm">Todos los comprobantes adjuntos a tus transacciones</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/40 border-zinc-800 mb-6">
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <Filter className="h-4 w-4" /> Filtros:
            </div>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="filter-month">
                <SelectValue placeholder="Mes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {facets.months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 w-48" data-testid="filter-category">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {facets.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 w-32" data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="expense">Gastos</SelectItem>
                <SelectItem value="income">Ingresos</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={clearFilters} data-testid="clear-filters">
                <XCircle className="h-4 w-4 mr-1" /> Limpiar
              </Button>
            )}
            <div className="ml-auto text-right text-sm">
              <p className="text-zinc-400">{items.length} recibo(s)</p>
              {items.length > 0 && (
                <p className="text-zinc-500 text-xs">
                  Total: <span className="text-red-400">{fmt(totalExpense)}</span> en gastos
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Grid */}
        {loading ? (
          <p className="text-zinc-500">Cargando recibos…</p>
        ) : items.length === 0 ? (
          <Card className="bg-gradient-to-br from-violet-500/10 to-pink-500/5 border-violet-500/30">
            <CardContent className="p-10 text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-violet-500/20 items-center justify-center mb-4">
                <ImageIcon className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {hasFilters ? 'Sin recibos para estos filtros' : 'Aún no tienes recibos'}
              </h3>
              <p className="text-zinc-400 mb-6">
                {hasFilters
                  ? 'Prueba a ajustar los filtros o limpiar la selección.'
                  : 'Adjunta recibos a tus transacciones desde el dashboard o el editor de transacciones — aparecerán aquí automáticamente.'}
              </p>
              {hasFilters && (
                <Button className="bg-violet-600 hover:bg-violet-700" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4" data-testid="receipts-grid">
            {items.map(t => (
              <button
                key={t.id}
                onClick={() => openReceipt(t)}
                className="group rounded-xl border border-zinc-800 bg-zinc-900/50 hover:border-violet-500/60 hover:bg-zinc-900 transition-all overflow-hidden text-left"
                data-testid={`receipt-card-${t.id}`}
              >
                <div className="aspect-square bg-zinc-800 flex items-center justify-center relative">
                  {t.receipt_kind === 'image'
                    ? <FileImage className="h-10 w-10 text-zinc-500 group-hover:text-violet-400" />
                    : t.receipt_kind === 'pdf'
                    ? <FileText className="h-10 w-10 text-zinc-500 group-hover:text-violet-400" />
                    : <ImageIcon className="h-10 w-10 text-zinc-500 group-hover:text-violet-400" />}
                  <span className={`absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded-full ${t.type === 'income' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                    {t.type === 'income' ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />}
                  </span>
                  {t.installments_total > 1 && (
                    <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">
                      {t.installments_current}/{t.installments_total}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-xs text-zinc-500">{t.date}</p>
                  <p className="text-sm font-medium text-white truncate">{t.description || t.category}</p>
                  <p className="text-xs text-zinc-400 truncate">{t.category}</p>
                  <p className={`text-sm font-bold mt-1 ${t.type === 'income' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {t.type === 'income' ? '+' : '-'}{fmt(t.amount)}
                  </p>
                  {t.account_id && accountMap[t.account_id] && (
                    <p className="text-[10px] mt-1 truncate" style={{ color: accountMap[t.account_id].color }}>
                      {accountMap[t.account_id].name}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Receipt detail lightbox */}
        {openTx && (
          <div
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
            onClick={closeReceipt}
            role="dialog"
            aria-label="Detalle del recibo"
            data-testid="receipt-detail-modal"
          >
            <div
              className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{openTx.description || openTx.category}</p>
                  <p className="text-xs text-zinc-500">{openTx.date} · {openTx.category} · <span className={openTx.type === 'income' ? 'text-emerald-400' : 'text-red-400'}>{openTx.type === 'income' ? '+' : '-'}{fmt(openTx.amount)}</span></p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {openContent?.receipt_base64 && (
                    <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-zinc-200" onClick={downloadReceipt} data-testid="download-receipt-btn">
                      <Download className="h-4 w-4 mr-2" /> Descargar
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white" onClick={closeReceipt} aria-label="Cerrar">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              <div className="p-4">
                {contentLoading && <p className="text-zinc-500 text-center py-12">Cargando recibo…</p>}
                {!contentLoading && openContent?.receipt_base64 && (
                  openContent.receipt_base64.startsWith('data:application/pdf')
                    ? (
                      <embed
                        src={openContent.receipt_base64}
                        type="application/pdf"
                        className="w-full h-[70vh] rounded"
                      />
                    ) : (
                      <img
                        src={openContent.receipt_base64}
                        alt={`Recibo ${openTx.id}`}
                        className="w-full max-h-[70vh] object-contain rounded"
                      />
                    )
                )}
                {!contentLoading && !openContent?.receipt_base64 && (
                  <p className="text-red-400 text-sm text-center py-12">No se pudo cargar el recibo.</p>
                )}
                {openTx.notes && (
                  <p className="text-zinc-400 text-sm mt-4 italic">"{openTx.notes}"</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReceiptsPage;
